import dayjs from 'dayjs';
import { Filter } from '../types/Filter';
import { options } from 'config/config';
import type { FormInstance } from 'antd';
import type { FieldData } from 'rc-field-form/lib/interface';
import { DEFAULT_PAGE, writePagination } from './listPagination';

interface FilterResetOptions {
  preserveOrdering?: boolean;
}

export const clearFilterValues = (
  filter: Filter | Record<string, unknown>
): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(filter || {}).map(([key, value]) => [key, value || null])
  );

export const addFilterEntriesToParams = (
  params: URLSearchParams,
  filter: Filter | Record<string, unknown>
) => {
  Object.entries(filter || {}).forEach(([key, value]) => {
    if (
      value !== null &&
      value !== undefined &&
      value !== '' &&
      !(Array.isArray(value) && value.length === 0)
    ) {
      params.set(key, Array.isArray(value) ? value.join(',') : String(value));
    }
  });
};

export const searchParamsEqual = (
  old: URLSearchParams,
  newParams: URLSearchParams
): boolean => {
  const keys = new Set<string>();
  old.forEach((_, key) => {
    keys.add(key);
  });
  newParams.forEach((_, key) => {
    keys.add(key);
  });
  let match = true;
  keys.forEach((k) => {
    if (old.get(k) !== newParams.get(k)) {
      match = false;
    }
  });
  return match;
};

export const buildFilterChangeParams = (options: {
  filter: Filter | Record<string, unknown>;
  search?: string;
  pageSize: number;
}): URLSearchParams => {
  const params = new URLSearchParams();
  addFilterEntriesToParams(params, clearFilterValues(options.filter));
  if (options.search) {
    params.set('search', options.search);
  }
  return writePagination(params, {
    page: DEFAULT_PAGE,
    pageSize: options.pageSize,
  });
};

export type MapDataType = 'farms' | 'products';

export const DEFAULT_MAP_DATA_TYPE: MapDataType = 'farms';

export const buildMapNavigationParams = (options: {
  dataType: MapDataType;
  page: number;
  pageSize: number;
  filter?: Filter | Record<string, unknown>;
  search?: string;
}): URLSearchParams => {
  const params = new URLSearchParams();
  params.set('dataType', options.dataType);
  writePagination(params, { page: options.page, pageSize: options.pageSize });
  addFilterEntriesToParams(params, options.filter || {});
  if (options.search) {
    params.set('search', options.search);
  }
  return params;
};

export const buildListNavigationParams = (options: {
  page: number;
  pageSize: number;
  filter?: Filter | Record<string, unknown>;
  search?: string;
}): URLSearchParams => {
  const params = new URLSearchParams();
  writePagination(params, { page: options.page, pageSize: options.pageSize });
  addFilterEntriesToParams(params, options.filter || {});
  if (options.search) {
    params.set('search', options.search);
  }
  return params;
};

export const applyDefaultDistanceRadius = (
  form: FormInstance<Filter>,
  marketType: string
): Filter => {
  const currentDistanceRadius = form.getFieldValue('distance_radius');
  const filter = form.getFieldsValue() as Filter;
  const hasLocation = Array.isArray(filter.location)
    ? filter.location.length > 0
    : !!filter.location;
  const hasAddress = !!filter.lks_lat && !!filter.lks_long;

  if (
    marketType === 'WHOLESALE' &&
    (hasLocation || hasAddress) &&
    !filter.distance_radius
  ) {
    filter.distance_radius = options.filters.defaultDistanceRadius;
  }

  if (filter.distance_radius !== currentDistanceRadius) {
    form.setFieldValue('distance_radius', filter.distance_radius);
  }

  return filter;
};

const LOCATION_FIELDS = ['location', 'location_radius'];

const fieldHasValue = (value: unknown): boolean =>
  Array.isArray(value) ? value.length > 0 : !!value;

export const clearAddressWhenLocationChosen = (
  form: FormInstance<Filter>,
  changedFields?: FieldData[]
): void => {
  const locationChosen = changedFields?.some((field) => {
    const fieldName = Array.isArray(field.name) ? field.name[0] : field.name;
    return (
      LOCATION_FIELDS.includes(String(fieldName)) && fieldHasValue(field.value)
    );
  });

  if (locationChosen && form.getFieldValue('address')) {
    form.setFieldsValue({ address: null, lks_lat: null, lks_long: null });
  }
};

export const getOrderingFilterValues = (filter: Filter): Filter => {
  const orderingFilter: Filter = {};

  if (filter.order_by) {
    orderingFilter.order_by = filter.order_by;
  }

  if (filter.order_direction) {
    orderingFilter.order_direction = filter.order_direction;
  }

  return orderingFilter;
};

export const getFilterValuesForReset = (
  filter: Filter,
  options: FilterResetOptions = {}
): Filter => {
  if (options.preserveOrdering) {
    return getOrderingFilterValues(filter);
  }

  return {};
};

const DATE_DDMMYYYY = /^(\d{2})\.(\d{2})\.(\d{4})$/;

export const parseFilterDefaults = (params: Record<string, any>) => {
  const parsed: Record<string, any> = {};
  Object.entries(params).forEach(([key, value]) => {
    if (!value) {
      parsed[key] = value;
      return;
    }

    if (key === 'year') {
      const yearValue = dayjs.isDayjs(value) ? value.year() : value;
      parsed[key] = dayjs(`${yearValue}`, 'YYYY');
    } else if (key === 'month') {
      const yearParam = params.year;
      const yearValue = dayjs.isDayjs(yearParam) ? yearParam.year() : yearParam;
      const year = yearValue ? dayjs(`${yearValue}`, 'YYYY') : dayjs();
      parsed[key] = year.month(Number(value) - 1).date(1);
    } else if (typeof value === 'string') {
      const match = value.match(DATE_DDMMYYYY);
      if (match) {
        const [, dd, mm, yyyy] = match;
        const parsedDate = dayjs(`${yyyy}-${mm}-${dd}`);
        parsed[key] = parsedDate.isValid() ? parsedDate : value;
      } else {
        parsed[key] = value;
      }
    } else {
      parsed[key] = value;
    }
  });
  return parsed;
};
